import { MapPin, Sparkles } from 'lucide-react';
import type { AiPreviewScreen } from '@/features/campaigns/types/campaign';
import { formatCount, formatMoney } from '@/shared/lib/format/money';
import { formatAvgViews, screenReason } from './aiPreviewHelpers';

export function MetricCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'brand' | 'success' | 'warn';
}) {
  const toneClass =
    tone === 'brand'
      ? 'border-[var(--dash-brand)]/20 bg-[var(--dash-brand-soft)]'
      : tone === 'success'
        ? 'border-[var(--dash-success)]/20 bg-[var(--dash-success-soft)]'
        : tone === 'warn'
          ? 'border-amber-200 bg-amber-50'
          : 'border-[var(--dash-line)] bg-[var(--dash-soft)]';

  return (
    <div className={`rounded-xl border px-3.5 py-3 ${toneClass}`}>
      <p className="m-0 text-[11px] font-semibold tracking-wide text-[var(--dash-muted)] uppercase">
        {label}
      </p>
      {hint ? (
        <p className="m-0 mt-0.5 text-[10px] text-[var(--dash-muted)]">{hint}</p>
      ) : null}
      <p className="m-0 mt-1.5 text-base font-bold text-[var(--dash-ink)]">
        {value}
      </p>
    </div>
  );
}

export function ScreenCard({ screen }: { screen: AiPreviewScreen }) {
  const analytics = screen.analytics;
  const genderViews =
    analytics?.femaleViews != null || analytics?.maleViews != null
      ? Number(analytics?.femaleViews ?? 0) + Number(analytics?.maleViews ?? 0)
      : null;
  const totalViews = analytics?.totalViews ?? genderViews;
  const pricing = screen.pricing;

  return (
    <div className="flex flex-col justify-between rounded-2xl border border-[var(--dash-line)] bg-white p-4 text-left">
      <div className="space-y-2.5">
        <div>
          <p
            className="m-0 text-sm font-bold text-[var(--dash-ink)] break-all sm:break-words line-clamp-2"
            title={screen.screenName}
          >
            {screen.screenName || 'Screen'}
          </p>
          <p className="m-0 mt-1 flex items-center gap-1 text-xs text-[var(--dash-muted)]">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            {[screen.address?.city, screen.address?.state]
              .filter(Boolean)
              .join(', ') ||
              screen.address?.building ||
              '—'}
          </p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {screen.screenType ? (
            <span className="rounded-md bg-[var(--dash-brand-soft)] px-2 py-0.5 text-[10px] font-semibold text-[var(--dash-brand)] capitalize">
              {screen.screenType}
            </span>
          ) : null}
          {screen.VenueType ? (
            <span className="rounded-md bg-[var(--dash-accent-soft)] px-2 py-0.5 text-[10px] font-semibold text-[var(--dash-accent)] capitalize">
              {screen.VenueType}
            </span>
          ) : null}
        </div>

        <div className="rounded-xl border border-[var(--dash-brand)]/15 bg-[var(--dash-brand-soft)]/70 px-3 py-2">
          <p className="m-0 flex items-center gap-1 text-[10px] font-bold tracking-wide text-[var(--dash-brand)] uppercase">
            <Sparkles className="h-3 w-3" />
            Why AI picked this
          </p>
          <p className="m-0 mt-1 text-[11px] leading-relaxed text-[var(--dash-ink)]">
            {screenReason(screen)}
          </p>
        </div>

        {screen.address?.fullAddress ? (
          <p className="m-0 line-clamp-2 text-[11px] text-[var(--dash-muted)]">
            {screen.address.fullAddress}
          </p>
        ) : null}
      </div>

      <div className="mt-3 space-y-2 border-t border-[var(--dash-line)] pt-3">
        <div className="grid grid-cols-3 gap-2">
          <MiniStat
            label="Total views"
            value={totalViews != null ? formatCount(totalViews) : '—'}
          />
          <MiniStat
            label="Avg views"
            value={formatAvgViews(analytics?.avgViews)}
            tone="success"
          />
          <MiniStat
            label="Price / sec"
            value={
              screen.basePrice != null ? formatMoney(screen.basePrice) : '—'
            }
            tone="warn"
          />
        </div>
        {pricing ? (
          <div className="grid grid-cols-3 gap-2">
            <MiniStat
              label="Repetitions"
              value={
                pricing.totalRepetitions != null
                  ? formatCount(pricing.totalRepetitions)
                  : '—'
              }
            />
            <MiniStat
              label="Ad seconds"
              value={
                pricing.totalDuration != null
                  ? `${formatCount(pricing.totalDuration)}s`
                  : '—'
              }
              tone="success"
            />
            <MiniStat
              label="Screen cost"
              value={
                pricing.amountWithTax != null
                  ? formatMoney(pricing.amountWithTax)
                  : '—'
              }
              tone="warn"
            />
          </div>
        ) : null}
        <div className="flex gap-2">
          <div className="flex flex-1 items-center justify-center rounded-xl border border-pink-100 bg-[#FDF2F8] py-1.5 text-[11px] font-bold text-[#DB2777]">
            {analytics?.femaleViews ?? 0} Female
          </div>
          <div className="flex flex-1 items-center justify-center rounded-xl border border-blue-100 bg-[#EFF6FF] py-1.5 text-[11px] font-bold text-[#2563EB]">
            {analytics?.maleViews ?? 0} Male
          </div>
        </div>
      </div>
    </div>
  );
}

export function MiniStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'success' | 'warn';
}) {
  const valueClass =
    tone === 'success'
      ? 'text-[var(--dash-success)]'
      : tone === 'warn'
        ? 'text-amber-700'
        : 'text-[var(--dash-brand)]';

  return (
    <div className="rounded-xl border border-[var(--dash-line)] bg-[var(--dash-soft)] p-2 text-center">
      <p className="m-0 truncate text-[9px] font-bold tracking-wide text-[var(--dash-muted)] uppercase">
        {label}
      </p>
      <p className={`m-0 mt-0.5 truncate text-xs font-bold ${valueClass}`}>
        {value}
      </p>
    </div>
  );
}

export function Row({
  label,
  value,
  ok,
}: {
  label: string;
  value: string;
  ok: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[var(--dash-muted)]">{label}</span>
      <span
        className={`font-semibold ${
          ok ? 'text-[var(--dash-success)]' : 'text-amber-700'
        }`}
      >
        {value}
      </span>
    </div>
  );
}
